import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Brain,
  AlertTriangle,
  Target,
  Layers,
  ExternalLink,
  Sparkles,
  Zap,
  MemoryStick,
  Cpu,
  Copy,
  Check,
  Info,
  Box,
  Shapes,
  PersonStanding,
  Video,
  ScanText,
} from 'lucide-react';
import {
  mockTAO6InstanceDetails,
  TAO6PretrainedModel,
  TAO6TaskType,
} from '@/mock/gpu-instance';
import { TAO6ModelSelector, TAO6ModelCard } from '../../components/tao';

const TAOModelsPage = () => {
  const { instanceId } = useParams<{ instanceId: string }>();
  const [selectedModel, setSelectedModel] = useState<TAO6PretrainedModel | null>(null);
  const [modelCardOpen, setModelCardOpen] = useState(false);
  const [modelCardModel, setModelCardModel] = useState<TAO6PretrainedModel | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  const details = useMemo(() => mockTAO6InstanceDetails(instanceId || ''), [instanceId]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPath(text);
      setTimeout(() => setCopiedPath(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleViewModelCard = (model: TAO6PretrainedModel) => {
    setModelCardModel(model);
    setModelCardOpen(true);
  };

  const getTaskInfo = (task: TAO6TaskType) => {
    switch (task) {
      case 'object_detection':
        return {
          name: 'Object Detection',
          icon: Target,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
        };
      case 'image_classification':
        return {
          name: 'Image Classification',
          icon: Layers,
          color: 'text-blue-500',
          bgColor: 'bg-blue-500/10',
        };
      case 'instance_segmentation':
        return {
          name: 'Instance Segmentation',
          icon: Box,
          color: 'text-purple-500',
          bgColor: 'bg-purple-500/10',
        };
      case 'semantic_segmentation':
        return {
          name: 'Semantic Segmentation',
          icon: Shapes,
          color: 'text-cyan-500',
          bgColor: 'bg-cyan-500/10',
        };
      case 'pose_estimation':
        return {
          name: 'Pose Estimation',
          icon: PersonStanding,
          color: 'text-orange-500',
          bgColor: 'bg-orange-500/10',
        };
      case 'action_recognition':
        return {
          name: 'Action Recognition',
          icon: Video,
          color: 'text-pink-500',
          bgColor: 'bg-pink-500/10',
        };
      case 'ocr':
        return {
          name: 'OCR',
          icon: ScanText,
          color: 'text-amber-500',
          bgColor: 'bg-amber-500/10',
        };
    }
  };

  // Statistics
  const detectionModels = details.pretrainedModels.filter(m => m.task === 'object_detection').length;
  const classificationModels = details.pretrainedModels.filter(m => m.task === 'image_classification').length;
  const qatSupportedModels = details.pretrainedModels.filter(m => m.supportsQAT).length;

  return (
    <div className="container py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-green-500" />
            NGC Model Catalog
          </h1>
          <p className="text-muted-foreground">
            Pretrained models from NVIDIA NGC for TAO 6.x
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => window.open('https://catalog.ngc.nvidia.com/orgs/nvidia/teams/tao/models', '_blank')}
        >
          <ExternalLink className="h-4 w-4 mr-2" />
          Open NGC Catalog
        </Button>
      </div>

      {/* Model Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.pretrainedModels.length}</div>
            <div className="text-sm text-muted-foreground">Total Models</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{detectionModels}</div>
            <div className="text-sm text-muted-foreground">Detection</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{classificationModels}</div>
            <div className="text-sm text-muted-foreground">Classification</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{qatSupportedModels}</div>
            <div className="text-sm text-muted-foreground">QAT Support</div>
          </CardContent>
        </Card>
      </div>

      {/* Model Browser */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Models</CardTitle>
          <CardDescription>Browse and select pretrained models for your experiments</CardDescription>
        </CardHeader>
        <CardContent>
          <TAO6ModelSelector
            models={details.pretrainedModels}
            selectedModelId={selectedModel?.id}
            onSelectModel={setSelectedModel}
            onViewModelCard={handleViewModelCard}
          />
        </CardContent>
      </Card>

      {/* Selected Model Details */}
      {selectedModel && (
        <Card className="border-primary/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getTaskInfo(selectedModel.task).bgColor}`}>
                  {(() => {
                    const Icon = getTaskInfo(selectedModel.task).icon;
                    return <Icon className={`h-5 w-5 ${getTaskInfo(selectedModel.task).color}`} />;
                  })()}
                </div>
                <div>
                  <CardTitle>{selectedModel.displayName}</CardTitle>
                  <CardDescription>{selectedModel.description}</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                {selectedModel.supportsQAT && (
                  <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
                    <Sparkles className="h-3 w-3 mr-1" />
                    QAT
                  </Badge>
                )}
                {selectedModel.supportsKD && (
                  <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
                    <Brain className="h-3 w-3 mr-1" />
                    KD
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Performance Metrics */}
            <div className="grid grid-cols-4 gap-4">
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <Zap className="h-4 w-4" />
                  {selectedModel.accuracyMetric === 'mAP' ? 'mAP' : 'Top-1 Acc'}
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {selectedModel.accuracy.toFixed(1)}%
                </div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <MemoryStick className="h-4 w-4" />
                  VRAM Required
                </div>
                <div className="text-2xl font-bold">
                  {selectedModel.vramRequired} GB
                </div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground mb-1">
                  <Cpu className="h-4 w-4" />
                  Parameters
                </div>
                <div className="text-2xl font-bold">
                  {selectedModel.parameters}
                </div>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg text-center">
                <div className="text-sm text-muted-foreground mb-1">Input Size</div>
                <div className="text-2xl font-bold font-mono">
                  {selectedModel.inputSize}
                </div>
              </div>
            </div>

            {/* Model Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Backbone</div>
                  <code className="text-sm font-mono">{selectedModel.backbone}</code>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">Task Type</div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const info = getTaskInfo(selectedModel.task);
                      const Icon = info.icon;
                      return (
                        <>
                          <Icon className={`h-4 w-4 ${info.color}`} />
                          <span>{info.name}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-1">NGC Path</div>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono truncate">{selectedModel.ngcPath}</code>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 ml-2"
                      onClick={() => handleCopy(selectedModel.ngcPath)}
                    >
                      {copiedPath === selectedModel.ngcPath ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-sm text-muted-foreground mb-2">Supported Augmentations</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedModel.supportedAugmentations.map((aug) => (
                      <Badge key={aug} variant="secondary" className="text-xs">
                        {aug}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-3">
              <Card className={`${selectedModel.supportsQAT ? 'border-yellow-500/50 bg-yellow-500/5' : 'opacity-50'}`}>
                <CardContent className="pt-4 text-center">
                  <Sparkles className={`h-6 w-6 mx-auto mb-2 ${selectedModel.supportsQAT ? 'text-yellow-500' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">Quantization-Aware Training</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {selectedModel.supportsQAT ? 'Supported' : 'Not Supported'}
                  </div>
                </CardContent>
              </Card>
              <Card className={`${selectedModel.supportsKD ? 'border-purple-500/50 bg-purple-500/5' : 'opacity-50'}`}>
                <CardContent className="pt-4 text-center">
                  <Brain className={`h-6 w-6 mx-auto mb-2 ${selectedModel.supportsKD ? 'text-purple-500' : 'text-gray-400'}`} />
                  <div className="font-medium text-sm">Knowledge Distillation</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {selectedModel.supportsKD ? 'Supported' : 'Not Supported'}
                  </div>
                </CardContent>
              </Card>
              <Card className="border-green-500/50 bg-green-500/5">
                <CardContent className="pt-4 text-center">
                  <Cpu className="h-6 w-6 mx-auto mb-2 text-green-500" />
                  <div className="font-medium text-sm">Multi-GPU Training</div>
                  <div className="text-xs text-muted-foreground mt-1">Supported</div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                className="flex-1"
                variant="outline"
                onClick={() => handleViewModelCard(selectedModel)}
              >
                <Info className="h-4 w-4 mr-2" />
                View Full Model Card
              </Button>
              <Button
                className="flex-1"
                variant="outline"
                onClick={() =>
                  window.open(
                    `https://catalog.ngc.nvidia.com/orgs/nvidia/teams/tao/models/${selectedModel.name}`,
                    '_blank'
                  )
                }
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View on NGC Catalog
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Model Card Dialog */}
      <TAO6ModelCard
        model={modelCardModel}
        open={modelCardOpen}
        onOpenChange={setModelCardOpen}
        onSelectModel={(model) => {
          setSelectedModel(model);
          setModelCardOpen(false);
        }}
      />

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> These models are from the NVIDIA NGC catalog.
          In production, models will be downloaded automatically when you create an experiment.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default TAOModelsPage;
